<template>
  <div class="member-registration-review-page">
    <hr class="separator " style="margin-top: 1px"/>

    <div>
      <div class="custom-text">
        <span class="user-manage">{{ $t('MemberRegistrationReview.title') }}</span>
      </div>
      <hr class="separator"/>
    </div>

    <SearchBar @search="handleSearch"/>
    <DataTable :data="tableData" :loading="loading" @refresh="loadData"/>
    <div class="pagination">
      <PaginationBar :totalRecords="totalRecords" :current-page="currentPage" @page-change="handlePageChange"
                     @page-size-change="handlePageSizeChange" :page-size="pageSize"/>
    </div>
  </div>
</template>

<script>
import SearchBar from './components/SearchBar.vue';
import DataTable from './components/DataTable.vue';
import PaginationBar from '@/components/PaginationBar.vue';
import { getMemberApplyList } from '@/api/api';

export default {
  components: {
    SearchBar,
    DataTable,
    PaginationBar,
  },
  data() {
    return {
      tableData: [],
      totalRecords: 0,
      currentPage: 1,
      pageSize: 10,
      isSearching: false,
      searchParams: {},
      loading: false,
      userPermissions: [],
    };
  },
  
  computed: {},

  created() {
    this.loadData();
  },
  
  mounted() {
    this.loadUserPermissions();
  },
  watch: {
    '$i18n.locale': function () {
      if (this.isSearching) {
        this.handleSearch(this.searchParams);
      } else {
        this.loadData();
      }
    },
  },
  methods: {
    async loadUserPermissions() {
      try {
        const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId');
        const cacheKey = `userPermissions_${userId}`;
        const cache = localStorage.getItem(cacheKey) || sessionStorage.getItem(cacheKey);
        if (cache) {
          this.userPermissions = JSON.parse(cache);
        }
      } catch (error) {
        console.error('Error accessing storage:', error);
        this.userPermissions = [];
      }
    },
    
    hasPermission(permission) {
      if (!this.userPermissions || !Array.isArray(this.userPermissions)) {
        return false;
      }
      
      const hasPermissionRecursive = (permissions) => {
        return permissions.some(perm => {
          if (perm.code === permission) {
            return true;
          }
          if (perm.children && Array.isArray(perm.children)) {
            return hasPermissionRecursive(perm.children);
          }
          return false;
        });
      };
      
      return hasPermissionRecursive(this.userPermissions);
    },
    
    async handleSearch(searchParams) {
      const page = this.currentPage;
      const pageSize = this.pageSize;

      this.isSearching = true;
      this.searchParams = searchParams;
      this.loading = true;
      try {
        const response = await getMemberApplyList(page, pageSize, searchParams);
        if (response && response.data) {
          this.tableData = response.data.map(item => this.mapResponseFields(item));
          this.totalRecords = response.total || 0;
          this.currentPage = response.current_page || page;
          this.pageSize = response.per_page || pageSize;
        }
      } catch (error) {
        console.error(this.$t('ProductManage.searchFailed'), error);
      } finally {
        this.loading = false;
      }
    },

    async handlePageChange(newPage) {
      try {
        this.currentPage = newPage;
        if (this.isSearching) {
          await this.handleSearch(this.searchParams);
        } else {
          await this.loadData();
        }
      } catch (error) {
        console.error(this.$t('ProductManage.loadDataFailed'));
      }
    },

    async handlePageSizeChange(newPageSize) {
      try {
        this.pageSize = newPageSize;
        await this.handleSearch(this.searchParams);
      } catch (error) {
        console.error(this.$t('ProductManage.loadDataFailed'));
      }
    },

    async loadData() {
      this.isSearching = false;
      const page = this.currentPage;
      const pageSize = this.pageSize;
      this.loading = true;
      try {
        const response = await getMemberApplyList(page, pageSize, {});
        if (response && response.data) {
          this.tableData = response.data.map(item => this.mapResponseFields(item));
          this.totalRecords = response.total || 0;
          this.currentPage = response.current_page || page;
          this.pageSize = response.per_page || pageSize;
        }
      } catch (error) {
        console.error(this.$t('ProductManage.loadDataFailed'), error);
      } finally {
        this.loading = false;
      }
    },

    generateMockData(page, pageSize, searchParams) {
      const total = 50;
      const start = (page - 1) * pageSize;
      const end = Math.min(start + pageSize, total);
      
      const statusMap = { 0: 'pending', 1: 'approved', 2: 'rejected' };
      let filteredStatuses = ['pending', 'approved', 'rejected'];
      
      if (searchParams && searchParams['status-eq'] !== undefined) {
        filteredStatuses = [statusMap[searchParams['status-eq']] || 'pending'];
      }
      
      const sources = ['展会', '杂志', '社交媒体', '供应商-自行车品牌'];
      const reasons = ['品质价格水平', '差异化', '供应商建议'];
      
      const data = [];
      const contactNames = ['张三', '李四', '王五', '赵六', '钱七'];
      const storeNames = ['阳光自行车店', '极速骑行', '绿色出行', '快乐单车', '骑行天地'];
      const countries = ['中国', '美国', '德国', '日本', '法国'];
      const locations = ['北京市朝阳区', '上海市浦东新区', '广州市天河区', '深圳市南山区', '杭州市西湖区'];
      
      for (let i = start; i < end; i++) {
        const statusIndex = Math.floor(Math.random() * filteredStatuses.length);
        const status = filteredStatuses[statusIndex];
        
        let contactName = contactNames[i % 5];
        let storeName = storeNames[i % 5];
        
        if (searchParams && searchParams['contactName-like'] && !contactName.includes(searchParams['contactName-like'])) {
          contactName = searchParams['contactName-like'] + i;
        }
        if (searchParams && searchParams['storeName-like'] && !storeName.includes(searchParams['storeName-like'])) {
          storeName = searchParams['storeName-like'] + i;
        }
        
        data.push({
          id: i + 1,
          contactName: contactName,
          storeName: storeName,
          email: `user${i + 1}@example.com`,
          tel: `1380013800${i % 10}`,
          country: countries[i % 5],
          postCode: `10000${i % 10}`,
          location: locations[i % 5],
          taxNumber: `TAX${100000 + i}`,
          website: `https://www.store${i + 1}.com`,
          workWithAnanda: i % 2 === 0 ? 'yes' : 'no',
          howKnowAnanda: sources[i % 4],
          whyChooseAnanda: reasons[i % 3],
          additionalInfo: i % 3 === 0 ? '希望能够获得更好的服务支持' : '',
          subscribeNewsletter: i % 2 === 0,
          officeArea: ['50-100㎡', '100-200㎡', '200-500㎡', '500㎡以上'][i % 4],
          warehouse: ['50-100㎡', '100-200㎡', '200-500㎡', '无仓库'][i % 4],
          staff: ['1-5人', '5-10人', '10-20人', '20人以上'][i % 4],
          serviceCapabilities: ['服务热线', '5天/8小时服务', '自行车维修能力'],
          note: i % 4 === 0 ? '需要尽快审核' : '',
          applicantStatus: status,
          statusText: this.getStatusText(status),
          applyTime: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
          reviewer: status !== 'pending' ? `审核员${i % 3 + 1}` : '',
          reviewTime: status !== 'pending' ? new Date(Date.now() - Math.random() * 3 * 24 * 60 * 60 * 1000).toISOString() : '',
          reviewComment: status === 'approved' ? '资料齐全，审核通过' : (status === 'rejected' ? '资料不全，需要补充' : '')
        });
      }
      
      return {
        data,
        total,
        currentPage: page,
        pageSize
      };
    },

    getStatusText(status) {
      const numericStatus = Number(status);
      const statusMap = {
        0: this.$t('MemberRegistrationReview.statusPending'),
        1: this.$t('MemberRegistrationReview.statusApproved'),
        2: this.$t('MemberRegistrationReview.statusRejected'),
        'pending': this.$t('MemberRegistrationReview.statusPending'),
        'approved': this.$t('MemberRegistrationReview.statusApproved'),
        'rejected': this.$t('MemberRegistrationReview.statusRejected')
      };
      return statusMap[numericStatus] || statusMap[status] || status;
    },

    mapResponseFields(item) {
      const auditStatus = item.audit_status !== undefined ? item.audit_status : (item.applicantStatus || item.status);
      return {
        ...item,
        id: item.id || item.id,
        contactName: item.contactName || item.contact_name || '',
        storeName: item.storeName || item.store_name || '',
        email: item.email || '',
        tel: item.tel || item.phone || '',
        country: item.country || '',
        postCode: item.postCode || item.post_code || '',
        location: item.location || '',
        taxNumber: item.taxNumber || item.tax_number || '',
        website: item.website || '',
        businessLicense: item.businessLicense || item.business_license || '',
        officeArea: item.officeArea || item.office_area || '',
        warehouse: item.warehouse || item.warehouseArea || item.warehouse_area || '',
        staff: item.staff || item.staffCount || item.staff_count || '',
        serviceCapabilities: item.serviceCapabilities || item.serviceCapability || item.service_capability || [],
        note: item.note || '',
        workWithAnanda: item.workWithAnanda || (item.isUseAnanda === 1 ? 'yes' : 'no'),
        howKnowAnanda: item.howKnowAnanda || item.knowChannel || item.know_channel || '',
        whyChooseAnanda: item.whyChooseAnanda || item.cooperateReason || item.cooperate_reason || '',
        additionalInfo: item.additionalInfo || item.reasonExtra || item.reason_extra || '',
        subscribeNewsletter: item.subscribeNewsletter || (item.isSubscribe === 1),
        applicantStatus: auditStatus,
        statusText: this.getStatusText(auditStatus),
        applyTime: item.applyTime || item.apply_time || item.createdAt || item.created_at || '',
        reviewer: localStorage.getItem('userName') || '',
        reviewTime: item.reviewTime || item.auditTime || item.audit_time || '',
        reviewComment: item.reviewComment || item.auditComment || item.audit_comment || ''
      };
    }
  },
};
</script>
<style scoped>
@import "@/assets/css/publicStyle.css";

.pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  margin-bottom: 20px;
}
</style>